/**
 * Sistema de Cache Inteligente para Settings
 * 
 * Implementa cache com TTL, invalidação seletiva e gerenciamento de memória
 * conforme especificado no design da documentação.
 */

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos em milliseconds
const MAX_CACHE_SIZE = 50; // Máximo de empresas em cache (conforme design)
const CLEANUP_INTERVAL = 10 * 60 * 1000; // 10 minutos

class SettingsCache {
  constructor() {
    // Cache por empresa: { companyId: { data, timestamp, ttl } }
    this.cache = new Map();
    
    // Set de listeners para notificações de mudanças
    this.listeners = new Set();
    
    // Flag global para indicar carregamento em andamento
    this.loadingStates = new Map();
    
    // Métricas de performance
    this.metrics = {
      hits: 0,
      misses: 0,
      totalRequests: 0,
      lastCleanup: Date.now()
    };
  }

  /**
   * Adiciona listener para mudanças no cache
   * @param {Function} callback - Função que será chamada quando o cache for atualizado
   * @returns {Function} - Função para remover o listener
   */
  addListener(callback) {
    this.listeners.add(callback);
    
    // Retorna função de cleanup
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Notifica todos os listeners sobre mudanças
   * @param {number} companyId 
   * @param {Object} settings 
   */
  notifyListeners(companyId, settings) {
    this.listeners.forEach(listener => {
      try {
        listener(companyId, settings);
      } catch (error) {
        console.warn('❌ Erro ao notificar listener de settings cache:', error);
      }
    });
  }

  /**
   * Verifica se cache é válido para uma empresa
   * @param {number} companyId 
   * @returns {boolean}
   */
  isValid(companyId) {
    if (!companyId) return false;
    
    const cacheEntry = this.cache.get(companyId);
    if (!cacheEntry) return false;
    
    const now = Date.now();
    const age = now - cacheEntry.timestamp;
    const ttl = cacheEntry.ttl || CACHE_DURATION;
    
    return age < ttl;
  }

  /**
   * Obtém settings do cache se válido
   * @param {number} companyId 
   * @returns {Object|null}
   */
  get(companyId) {
    this.metrics.totalRequests++;
    
    if (!this.isValid(companyId)) {
      this.metrics.misses++;
      return null;
    }
    
    this.metrics.hits++;
    const cacheEntry = this.cache.get(companyId);
    
    console.log(`🎯 Cache HIT para empresa ${companyId}:`, {
      hitRate: ((this.metrics.hits / this.metrics.totalRequests) * 100).toFixed(1) + '%',
      settingsCount: Object.keys(cacheEntry.data).length
    });
    
    return cacheEntry.data;
  }

  /**
   * Armazena settings no cache
   * @param {number} companyId 
   * @param {Object} settings 
   * @param {number} customTTL - TTL customizado em ms
   */
  set(companyId, settings, customTTL = CACHE_DURATION) {
    if (!companyId || !settings) return;
    
    // Limpar cache se atingir limite máximo
    if (this.cache.size >= MAX_CACHE_SIZE) {
      this.cleanup();
    }
    
    const cacheEntry = {
      data: { ...settings },
      timestamp: Date.now(),
      ttl: customTTL,
      companyId
    };
    
    this.cache.set(companyId, cacheEntry);
    
    // Salvar no localStorage para persistência
    try {
      localStorage.setItem(
        `companySettings_${companyId}`, 
        JSON.stringify({
          ...cacheEntry,
          fromCache: true
        })
      );
    } catch (error) {
      console.warn('⚠️ Erro ao salvar settings no localStorage:', error);
    }
    
    console.log(`💾 Settings cacheados para empresa ${companyId}:`, {
      settingsCount: Object.keys(settings).length,
      ttl: customTTL,
      cacheSize: this.cache.size
    });
    
    // Notificar listeners
    this.notifyListeners(companyId, settings);
  }

  /**
   * Invalida cache de uma empresa específica
   * @param {number} companyId 
   */
  invalidate(companyId) {
    if (!companyId) return;
    
    this.cache.delete(companyId);
    
    // Remover do localStorage
    try {
      localStorage.removeItem(`companySettings_${companyId}`);
    } catch (error) {
      console.warn('⚠️ Erro ao remover settings do localStorage:', error);
    }
    
    console.log(`🗑️ Cache invalidado para empresa ${companyId}`);
  }

  /**
   * Limpa todo o cache
   */
  clear() {
    const companyIds = Array.from(this.cache.keys());
    
    this.cache.clear();
    this.loadingStates.clear();
    
    // Limpar localStorage
    companyIds.forEach(companyId => {
      try {
        localStorage.removeItem(`companySettings_${companyId}`);
      } catch (error) {
        console.warn('⚠️ Erro ao limpar localStorage:', error);
      }
    });
    
    console.log('🧹 Cache completamente limpo');
  }

  /**
   * Remove entradas expiradas do cache
   */
  cleanup() {
    const now = Date.now();
    const expiredEntries = [];
    
    for (const [companyId, entry] of this.cache.entries()) {
      const age = now - entry.timestamp;
      if (age >= (entry.ttl || CACHE_DURATION)) {
        expiredEntries.push(companyId);
      }
    }
    
    // Remover entradas expiradas
    expiredEntries.forEach(companyId => {
      this.invalidate(companyId);
    });
    
    // Se ainda há muitas entradas, remover as mais antigas
    if (this.cache.size >= MAX_CACHE_SIZE) {
      const entries = Array.from(this.cache.entries());
      entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
      
      const toRemove = Math.ceil(MAX_CACHE_SIZE / 2);
      for (let i = 0; i < toRemove && i < entries.length; i++) {
        this.invalidate(entries[i][0]);
      }
    }
    
    this.metrics.lastCleanup = now;
    
    if (expiredEntries.length > 0) {
      console.log(`🧹 Cleanup do cache: ${expiredEntries.length} entradas removidas`);
    }
  }

  /**
   * Carrega settings do localStorage como fallback
   * @param {number} companyId 
   * @returns {Object|null}
   */
  loadFromLocalStorage(companyId) {
    if (!companyId) return null;
    
    try {
      const stored = localStorage.getItem(`companySettings_${companyId}`);
      if (!stored) return null;
      
      const data = JSON.parse(stored);
      
      // Verificar se não é muito antigo (máximo 1 hora)
      const maxAge = 60 * 60 * 1000; // 1 hora
      if (data.timestamp && (Date.now() - data.timestamp) > maxAge) {
        localStorage.removeItem(`companySettings_${companyId}`);
        console.log(`🗑️ LocalStorage expirado removido para empresa ${companyId}`);
        return null;
      }
      
      console.log(`💿 Settings carregados do localStorage para empresa ${companyId}`);
      return data.data || data;
      
    } catch (error) {
      console.warn('⚠️ Erro ao carregar settings do localStorage:', error);
      // Tentar limpar localStorage corrompido
      try {
        localStorage.removeItem(`companySettings_${companyId}`);
      } catch (cleanupError) {
        console.warn('⚠️ Erro ao limpar localStorage corrompido:', cleanupError);
      }
      return null;
    }
  }

  /**
   * Verifica se está carregando settings para uma empresa
   * @param {number} companyId 
   * @returns {boolean}
   */
  isLoading(companyId) {
    return this.loadingStates.get(companyId) === true;
  }

  /**
   * Define estado de carregamento
   * @param {number} companyId 
   * @param {boolean} loading 
   */
  setLoading(companyId, loading) {
    if (loading) {
      this.loadingStates.set(companyId, true);
    } else {
      this.loadingStates.delete(companyId);
    }
  }

  /**
   * Obtém métricas de performance do cache
   * @returns {Object}
   */
  getMetrics() {
    const hitRate = this.metrics.totalRequests > 0 
      ? (this.metrics.hits / this.metrics.totalRequests) * 100 
      : 0;
    
    return {
      ...this.metrics,
      hitRate: parseFloat(hitRate.toFixed(2)),
      cacheSize: this.cache.size,
      memoryUsage: this.estimateMemoryUsage()
    };
  }

  /**
   * Estima uso de memória do cache (aproximado)
   * @returns {number} - Tamanho estimado em KB
   */
  estimateMemoryUsage() {
    let size = 0;
    
    for (const [companyId, entry] of this.cache.entries()) {
      size += JSON.stringify(entry).length;
    }
    
    return Math.round(size / 1024); // KB
  }

  /**
   * Inicia limpeza automática periódica
   */
  startAutoCleanup() {
    // Limpar cache a cada 10 minutos (conforme design)
    setInterval(() => {
      this.cleanup();
    }, CLEANUP_INTERVAL);
  }
}

// Instância singleton do cache
const settingsCache = new SettingsCache();

// Iniciar limpeza automática
settingsCache.startAutoCleanup();

export default settingsCache;